import { useState } from "react"
import { Box, Link } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardSideDrawer } from "../drawers/Drawers"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"

import { ChatMessageAuthorRole, ChatMessageSender } from "./orderChatTypes"
import { useOrderChat } from "./useOrderChat"
import { OrderChatMessageList } from "./OrderChatMessageList"
import { OrderChatInput } from "./OrderChatInput"
import { OrderChatDisclaimer, isDisclaimerAccepted } from "./OrderChatDisclaimer"

const useStyles = makeStyles()((theme) => ({
    body: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
    },
    disclaimer: {
        flexShrink: 0,
        marginBottom: "1rem",
    },
    messageList: {
        flex: 1,
        overflow: "auto",
        minHeight: 0,
    },
    input: {
        flexShrink: 0,
    },
    subtitleLink: {
        color: theme.palette.primary.main,
        cursor: "pointer",
        textDecoration: "underline",
        fontWeight: 400,
        fontSize: "1rem",
    },
}))

type OrderChatDrawerProps = Readonly<{
    open: boolean
    onClose: () => void
    orderId: number
    orderTitle?: string
    viewerRole: ChatMessageAuthorRole
    sender?: ChatMessageSender
    onMessageSent?: () => Promise<void> | void
    onSubtitleClick?: () => void
}>

export function OrderChatDrawer({
    open,
    onClose,
    orderId,
    orderTitle,
    viewerRole,
    sender,
    onMessageSent,
    onSubtitleClick,
}: OrderChatDrawerProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { messages, isLoading, sendMessage } = useOrderChat(orderId, sender, onMessageSent)
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(isDisclaimerAccepted)

    const placeholder = l10n.getString(
        viewerRole === ChatMessageAuthorRole.MERCHANT
            ? "order-chat-placeholder-merchant"
            : "order-chat-placeholder-customer",
    )

    const subtitleText = orderTitle ? `#${orderId} ${orderTitle}` : `#${orderId}`
    const subtitle = onSubtitleClick ? (
        <Link component="button" onClick={onSubtitleClick} className={classes.subtitleLink}>
            {subtitleText}
        </Link>
    ) : (
        subtitleText
    )

    return (
        <StandardSideDrawer
            name="order-chat"
            title={
                <LocalizedStrict id="order-chat-drawer-title">
                    <span>Order chat</span>
                </LocalizedStrict>
            }
            subtitle={subtitle}
            open={open}
            onClose={onClose}
            width="32.5rem"
        >
            <Box className={classes.body}>
                {!disclaimerAccepted && (
                    <Box className={classes.disclaimer}>
                        <OrderChatDisclaimer onAccept={() => setDisclaimerAccepted(true)} />
                    </Box>
                )}
                <Box className={classes.messageList}>
                    <OrderChatMessageList messages={messages} isLoading={isLoading} />
                </Box>
                <Box className={classes.input}>
                    <OrderChatInput
                        onSend={sendMessage}
                        disabled={!disclaimerAccepted}
                        placeholder={placeholder}
                    />
                </Box>
            </Box>
        </StandardSideDrawer>
    )
}
